import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { formatDate } from "@/lib/date-utils";
import { Package, Calendar, User } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { useProductsLookup } from "@/hooks/use-products";

interface OrderTemplateViewModalProps {
  template: any | null;
  isOpen: boolean;
  onClose: () => void;
  onUseTemplate?: (templateId: string) => void;
}

export const OrderTemplateViewModal: React.FC<OrderTemplateViewModalProps> = ({
  template,
  isOpen,
  onClose,
  onUseTemplate,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();

  // Extract product IDs from template for lookup
  const templateProductIds = useMemo(() => {
    if (!template?.products) return [];
    return template.products.map((p: any) => p.productId).filter(Boolean);
  }, [template]);

  // Lookup products for template viewing
  const { products } = useProductsLookup(
    templateProductIds,
    templateProductIds.length > 0 && isOpen,
  );

  if (!template) return null;

  const templateProducts = Array.isArray(template.products)
    ? template.products
    : [];

  // Calculate totals using current pricing from products catalog
  const totalItems = templateProducts.reduce((sum: number, p: any) => {
    if (!p) return sum;
    return sum + (p.quantity || 1);
  }, 0);

  // Calculate total cost and determine if any products have TBD pricing
  let calculatedTotal = 0;
  let hasIncalculableItems = false;

  templateProducts.forEach((p: any) => {
    if (!p || !p.quantity) {
      hasIncalculableItems = true;
      return;
    }

    // Try to get current pricing from catalog
    const currentProduct = products?.find((cp) => cp.uid === p.productId);
    const currentPrice = currentProduct?.currentPrice;

    // Only add to total if we have both quantity and price
    if (currentPrice && currentProduct?.isActive) {
      calculatedTotal += p.quantity * currentPrice;
    } else {
      hasIncalculableItems = true;
    }
  });

  const totalCost = hasIncalculableItems ? "TBD" : calculatedTotal;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0">
        <div className="bg-white">
          {/* Professional Header */}
          <div className="border-b-2 border-primary/20 pb-6 mb-8 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  {template.name}
                </h1>
                <p className="text-slate-600 mt-2">{t("common.template")}</p>
              </div>
              <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                {t("components.orders.orderTemplateViewModal.templatePreview")}
              </div>
            </div>
          </div>

          <div className="px-6 space-y-8">
            {/* Template Information Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg">
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  {t("components.orders.orderTemplateViewModal.templateInformation")}
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm font-medium text-slate-600">
                      <Package className="h-4 w-4 mr-2" />
                      {t("components.orders.orderTemplateViewModal.numberOfItems")}
                    </div>
                    <div className="text-base font-medium text-slate-900">
                      {totalItems}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm font-medium text-slate-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t("components.orders.orderTemplateViewModal.createdOn")}
                    </div>
                    <div className="text-base font-medium text-slate-900">
                      {template.createdAt
                        ? formatDate(template.createdAt)
                        : t("components.orders.orderTemplateViewModal.nA")}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm font-medium text-slate-600">
                      <User className="h-4 w-4 mr-2" />
                      {t("components.orders.orderTemplateViewModal.createdBy")}
                    </div>
                    <div className="text-base font-medium text-slate-900">
                      {template.createdByName || user?.fullName || t("common.unknown")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {template.description && (
              <div className="border border-slate-200 rounded-lg">
                <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {t("common.description")}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-700 leading-relaxed">
                    {template.description}
                  </p>
                </div>
              </div>
            )}

            {/* Products */}
            {templateProducts.length > 0 && (
              <div className="border border-slate-200 rounded-lg">
                <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                    <Package className="h-4 w-4 mr-2" />
                    {t("components.orders.orderTemplateViewModal.products")}{templateProducts.length})
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {templateProducts.map(
                      (templateProduct: any, index: number) => {
                        if (!templateProduct) {
                          return (
                            <div
                              key={index}
                              className="flex justify-between items-center py-3 border-b border-slate-100 last:border-b-0"
                            >
                              <div className="flex-1">
                                <div className="font-medium text-slate-900">
                                  {t("components.orders.orderTemplateViewModal.unknownProduct")}
                                </div>
                                <div className="text-sm text-slate-600 mt-1">
                                  {t("components.orders.orderTemplateViewModal.idNA")}
                                </div>
                                <div className="text-sm text-red-600 mt-1">
                                  {t("components.orders.orderTemplateViewModal.productUnavailable")}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium text-slate-900">
                                  0 × $0.00
                                </div>
                                <div className="text-lg font-semibold text-primary">
                                  $0.00
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Try to get current product data from catalog
                        const currentProduct = products?.find(
                          (cp) => cp.uid === templateProduct.productId,
                        );
                        const quantity = templateProduct.quantity || "TBD";

                        // Use current pricing if product exists, fallback to TBD
                        let unitPrice: number | "TBD" = "TBD";
                        let productStatus: string | null = null;

                        if (currentProduct && currentProduct.isActive) {
                          // Product exists and is active - use current price
                          unitPrice = currentProduct.currentPrice || "TBD";
                        } else {
                          // Product doesn't exist or is inactive - indicate issue
                          productStatus = "Product unavailable";
                        }

                        return (
                          <div
                            key={index}
                            className="flex justify-between items-center py-3 border-b border-slate-100 last:border-b-0"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">
                                {templateProduct.description ||
                                  t("components.orders.orderTemplateViewModal.unknownProduct")}
                              </div>
                              <div className="text-sm text-slate-600 mt-1">
                                {t("common.id")} {currentProduct?.hajocaProductId || t("components.orders.orderTemplateViewModal.nA")}
                              </div>
                              {productStatus && (
                                <div className="text-sm text-red-600 mt-1">
                                  {productStatus}
                                </div>
                              )}
                              {templateProduct.notes && (
                                <div className="text-sm text-slate-600 mt-1">
                                  {t("components.orders.orderTemplateViewModal.notes")} {templateProduct.notes}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-slate-900">
                                {quantity} ×{" "}
                                {unitPrice === "TBD"
                                  ? t("components.orders.orderTemplateViewModal.tbd")
                                  : `$${formatCentsAsDollars(unitPrice)}`}
                              </div>
                              <div className="text-lg font-semibold text-primary">
                                {unitPrice === "TBD" || quantity === "TBD"
                                  ? t("components.orders.orderTemplateViewModal.tbd")
                                  : `$${formatCentsAsDollars((quantity as number) * (unitPrice as number))}`}
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <div className="flex justify-between items-center bg-primary/10 p-4 rounded">
                <span className="text-lg font-semibold text-slate-900">
                  {t("components.orders.orderTemplateViewModal.estimatedTotalCost")}
                </span>
                <span className="text-2xl font-bold text-primary">
                  {totalCost === "TBD"
                    ? t("components.orders.orderTemplateViewModal.tbd")
                    : `$${formatCentsAsDollars(totalCost)}`}
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-2 text-center">
                {t("components.orders.orderTemplateViewModal.pricesShownAreEstimates")}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 px-6 pb-6 pt-4 border-t border-slate-200 mt-8">
              <Button
                variant="outline"
                onClick={onClose}
                size="lg"
                className="flex-1"
              >
                {t("common.close")}
              </Button>

              {onUseTemplate && (
                <Button
                  onClick={() => onUseTemplate(template.uid)}
                  size="lg"
                  variant="default"
                  className="flex-1"
                >
                  {t("components.orders.orderTemplateViewModal.edit")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
